import * as Sentry from '@sentry/react';

/**
 * Service managing cart business logic
 */
export class CartService {
  constructor() {
    this.ELEMENT_IDS = {
      FOOTER_CART: 'footerCart',
      FOOTER_CART_ALT: 'footer-cart',
      PRODUCT_IMG: 'product_img'
    };
  }

  /**
   * Validate required parameters for adding to cart
   * @param {Object} params - Add to cart parameters
   * @returns {Object} Validation result with success and message
   */
  validateAddToCartParams(params) {
    const { cartItems, product, dispatch, addToCart, axiosInstance } = params;

    if (!cartItems?._id) {
      return {
        success: false,
        message: 'Missing cart ID'
      };
    }

    if (!product) {
      return {
        success: false,
        message: 'Missing product'
      };
    }

    if (!dispatch || typeof dispatch !== 'function') {
      return {
        success: false,
        message: 'Missing dispatch function'
      };
    }

    if (!addToCart || typeof addToCart !== 'function') {
      return {
        success: false,
        message: 'Missing addToCart action'
      };
    }

    if (!axiosInstance) {
      return {
        success: false,
        message: 'Missing axios instance'
      };
    }

    return {
      success: true,
      message: 'Valid parameters'
    };
  }

  /**
   * Add product to cart via Redux
   * @param {Object} params - Add to cart parameters
   */
  addProductToCart(params) {
    const { cartItems, product, qty, dispatch, addToCart, axiosInstance } = params;
    
    try {
      Sentry.setTag('orderId', cartItems._id);
      dispatch(addToCart(cartItems._id, product, qty, axiosInstance));
    } catch (error) {
      console.error('Error adding product to cart:', error);
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Clean up state after adding to cart
   * @param {Function} setProduct - Function to reset product
   * @param {Function} setCode - Function to reset code
   */
  cleanupAfterAddToCart(setProduct, setCode) {
    try {
      if (typeof setProduct === 'function') {
        setProduct(false);
      }
      
      if (typeof setCode === 'function') {
        setCode(0);
      }
    } catch (error) {
      console.error('Error during cleanup:', error);
      Sentry.captureException(error);
    }
  }

  /**
   * Check if animation should be performed
   * @returns {boolean} True if footer cart element exists
   */
  shouldAnimateAddToCart() {
    const footerCart = document.getElementById(this.ELEMENT_IDS.FOOTER_CART);
    return !!footerCart;
  }

  /**
   * Get footer cart DOM element
   * @returns {HTMLElement|null} Footer cart element or null
   */
  getFooterCartElement() {
    return document.getElementById(this.ELEMENT_IDS.FOOTER_CART) || 
           document.getElementById(this.ELEMENT_IDS.FOOTER_CART_ALT);
  }

  /**
   * Get product image DOM element
   * @returns {HTMLElement|null} Product image element or null
   */
  getProductImageElement() {
    return document.getElementById(this.ELEMENT_IDS.PRODUCT_IMG);
  }

  /**
   * Prepare necessary data for animation
   * @param {Object} product - Product to animate
   * @returns {Object|null} Animation data or null if not possible
   */
  prepareAnimationData(product) {
    if (!product) {
      return null;
    }

    const footerCart = this.getFooterCartElement();
    const productImg = this.getProductImageElement();

    if (!footerCart || !productImg) {
      return null;
    }

    return {
      footerCart,
      productImg,
      footerCartId: this.ELEMENT_IDS.FOOTER_CART,
      productImgId: this.ELEMENT_IDS.PRODUCT_IMG,
      cartAltId: this.ELEMENT_IDS.FOOTER_CART_ALT
    };
  }

  /**
   * Handle scan pause logic based on item limit
   * @param {number} countItems - Number of items in cart
   * @param {Function} setIsPaused - Function to control scan pause
   * @param {number} itemLimit - Item limit (optional, default: 4)
   */
  handleScanPauseLogic(countItems, setIsPaused, itemLimit = 4) {
    if (typeof setIsPaused === 'function' && countItems >= itemLimit) {
      setIsPaused(true);
    }
  }
}

export const cartService = new CartService(); 